import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import CustomersPage from "./pages/CustomersPage";
import UsersPage from "./pages/UsersPage";
import OrdersPage from "./pages/OrdersPage";
import MainLayout from "./components/Layout/MainLayout";
import { AuthProvider } from "./store/AuthContext";
import './i18n';
import ProtectedRoute from "./routes/ProtectedRoute";
import CitiesPage from "./pages/CityPage";
import RolePage from "./pages/RolePage";
import OrderStatusPage from "./pages/OrderStatusPage";
import ProfilePage from "./pages/ProfilePage";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { getDirection } from "./utils/i18n";
import { ConfigProvider, App as AntApp } from "antd";
import PrintInvoicePage from "./pages/PrintInvoicePage";
import ProductPage from "./pages/ProductPage";
import CurrencyPage from "./pages/CurrencyPage";
import SuppliersPage from "./pages/SuppliersPage";
import { SupplierProvider } from "./store/supplierContext";
import { CurrencyProvider } from "./store/currencyContext";
import PurchasePage from "./pages/PurchasePage";
import { PurchaseProvider } from "./store/purchaseContext";
import PurchaseIndex from "./pages/purchase/PurchaseIndex";
import SaleIndexPage from "./pages/Sale/SaleIndexPage";
import LoanRepaymentIndex from "./pages/LoanRepayment/LoanRepaymentIndex";
import SupplierPaymentIndex from "./pages/SupplierPayment/SupplierPaymentIndex";
import ExpenseIndex from "./pages/Expense/ExpenseIndex";
import ReturnIndex from "./pages/Return/ReturnIndex";
import ReportIndex from "./pages/Report/ReportIndex";
import AuditLogPage from "./pages/AuditLogPage";
import ArchivePage from "./pages/ArchivePage";
import SettingsPage from "./pages/SettingsPage";
import AccessDeniedPage from "./pages/AccessDeniedPage";
import { PERMS } from "./constants/permissions";

// Modern Deep Blue + Mint Colors
const PRIMARY = "#22314A";      // Deep navy blue
const ACCENT = "#2EC4B6";       // Mint/Teal accent
const BG = "#F7F9FB";           // Light background
const SECONDARY = "#41506B";    // Slate for secondary
const SUCCESS = "#48C78E";      // Mint green for success
const WARNING = "#FFB200";      // Amber for warning
const ERROR = "#E84855";        // Coral red for error

const theme = {
  token: {
    colorPrimary: PRIMARY,         // Main deep blue highlight
    colorInfo: ACCENT,             // Mint accent
    colorSuccess: SUCCESS,         // Mint green
    colorWarning: WARNING,         // Amber
    colorError: ERROR,             // Coral red
    colorBgBase: BG,               // General background
    colorBgContainer: "#FFFFFF",   // For cards/panels/modals
    colorBgLayout: BG,             // Main page background
    colorBorder: "#E0E0E0",        // Soft border
    colorTextHeading: PRIMARY,     // Deep blue headings
    colorText: "#222",             // Dark text
    colorTextLightSolid: "#FFFFFF",// White text for buttons
    colorLink: ACCENT,             // Mint link color
    colorLinkActive: PRIMARY,
    colorLinkHover: "#19b4a3",     // Darker mint on hover
    fontSize: 15,
  },
  components: {
    Button: {
      fontWeight: 600,
      colorPrimary: ACCENT,              // Main CTA is mint
      colorPrimaryHover: "#19b4a3",      // Slightly darker mint
      colorPrimaryActive: "#139386",     // Even darker mint
      colorTextLightSolid: "#FFFFFF",    // White button text
      colorSuccess: SUCCESS,
      defaultBg: PRIMARY,                // Deep blue for secondary/default
      defaultColor: "#fff",
      defaultBorderColor: PRIMARY,
      defaultHoverBg: "#fff",
      defaultHoverColor: PRIMARY,
      defaultActiveBg: BG,
      boxShadow: "none",
    },
    Card: {
      boxShadow: "0 2px 12px 0 rgba(34,49,74,0.06)",  // Use deep blue in shadow
      colorBgContainer: "#FFFFFF",
      headerBg: "#FFFFFF",
      headerColor: PRIMARY,
      colorTextHeading: PRIMARY,
    },
    Table: {
      headerBg: PRIMARY,
      headerColor: "#FFFFFF",
      rowHoverBg: "#e0f7f4" // Very light mint on row hover
    },
    Menu: {
      colorItemBg: PRIMARY,            // Deep blue
      colorItemText: "#FFFFFF",
      colorItemBgActive: ACCENT,       // Mint for active item
      colorItemTextActive: "#FFFFFF",
      colorItemTextHover: "#FFFFFF",
    },
    Modal: {
      contentBg: "#FFFFFF",
      headerBg: PRIMARY,               // Deep blue modal header
      titleColor: "#FFFFFF",           // White modal title
      borderRadius: 12,
      boxShadow: "0 2px 12px 0 rgba(34,49,74,0.14)",
      colorIcon: PRIMARY,
    },
  }
};

const App: React.FC = () => {
  const { i18n } = useTranslation();
  const direction = getDirection(i18n.language);

  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
  }, [direction]);

  return (
    <ConfigProvider direction={direction} theme={theme}>
      <AntApp>
        <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<ProtectedRoute permission={PERMS.dashboard.view}><DashboardPage /></ProtectedRoute>} />
              <Route path="customers" element={<ProtectedRoute permission={PERMS.customer.list}><CustomersPage /></ProtectedRoute>} />
              <Route path="users" element={<ProtectedRoute permission={[PERMS.user.list, PERMS.dashboard.users]}><UsersPage /></ProtectedRoute>} />
              <Route path="orders/new" element={<ProtectedRoute permission="order.create"><OrdersPage /></ProtectedRoute>} />
              <Route path="city" element={<ProtectedRoute permission={[PERMS.city.list, PERMS.dashboard.city]}><CitiesPage /></ProtectedRoute>} />
              <Route path="roles" element={<ProtectedRoute permission={[PERMS.role.list, PERMS.dashboard.roles]}><RolePage /></ProtectedRoute>} />
              <Route path="orders/status/:status" element={<ProtectedRoute permission="order.list"><OrderStatusPage /></ProtectedRoute>} />
              <Route path="profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="access-denied" element={<AccessDeniedPage />} />
              <Route path="/print-invoice" element={<PrintInvoicePage />} />
              <Route path="/report/*" element={<ReportIndex />} />
              <Route path="/products" element={<ProtectedRoute permission={PERMS.product.list}><ProductPage /></ProtectedRoute>} />
              <Route path="/currency" element={<ProtectedRoute permission={PERMS.currency.list}><CurrencyPage /></ProtectedRoute>} />
              <Route
                path="/suppliers"
                element={
                  <ProtectedRoute permission={PERMS.supplier.list}>
                    <SupplierProvider>
                      <CurrencyProvider>
                        <SuppliersPage />
                      </CurrencyProvider>
                    </SupplierProvider>
                  </ProtectedRoute>
                }
              />
              <Route path="/purchase/*" element={<ProtectedRoute permission={[PERMS.purchase.list, PERMS.purchase.create]}><PurchaseIndex /></ProtectedRoute>} />
              <Route
                path="/sales/*"
                element={<ProtectedRoute permission={[PERMS.sell.list, PERMS.sell.create]}><SaleIndexPage /></ProtectedRoute>}
              />
              <Route path="/loan-repayments/*" element={<ProtectedRoute permission={[PERMS.loanRepayment.list, PERMS.loanRepayment.create]}><LoanRepaymentIndex /></ProtectedRoute>} />
              <Route path="/supplier-payments/*" element={<ProtectedRoute permission={[PERMS.supplierPayment.list, PERMS.supplierPayment.create]}><SupplierPaymentIndex /></ProtectedRoute>} />
              <Route path="/expenses/*" element={<ProtectedRoute permission={[PERMS.expense.list, PERMS.expense.create]}><ExpenseIndex /></ProtectedRoute>} />
              <Route path="/returns/*" element={<ProtectedRoute permission={[PERMS.returnInvoice.list, PERMS.returnInvoice.create]}><ReturnIndex /></ProtectedRoute>} />
              <Route path="/audit-logs" element={<ProtectedRoute permission={[PERMS.audit.list, PERMS.audit.read]}><AuditLogPage /></ProtectedRoute>} />
              <Route path="/archive" element={<ProtectedRoute permission={[PERMS.archive.list, PERMS.archive.read]}><ArchivePage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute permission={[PERMS.settings.read, PERMS.settings.backup]}><SettingsPage /></ProtectedRoute>} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Router>
      </AuthProvider>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
